import { relativeTime } from '../userAgent';
import type { ProjectProgress } from '../api';
import { useAccountDay } from '../accountDay';
import { readableInstant } from '../dateLabels';

/**
 * What is happening in this project, in one line.
 *
 * The row said `CSV export for the reports screen · Project · planning · tomorrow · Sep 1 → Sep 19
 * · UTC` on a project holding eight tasks with real statuses and four people working on it — every
 * word of it about what the project *is*, and not one about what it is doing. Two personas hit
 * that independently on the same screen, one on the list and one in the panel.
 *
 * The bar is a status mix rather than a single fill: "5 of 8 done" and "5 of 8 done, 1 of the rest
 * actually started" are different situations, and the second is the one worth a glance. It is
 * `aria-hidden` because the counts beside it say the same thing in words, and two readings of one
 * number is how a screen reader ends up saying it twice.
 */
export function ProjectProgressLine({ progress }: { progress: ProjectProgress }) {
  const { total, done, inProgress, parked } = progress;
  const { timezone } = useAccountDay();
  const share = (n: number) => (total === 0 ? 0 : Math.round((n / total) * 100));
  return (
    <p className="project-progress" data-testid="project-progress">
      {total === 0 ? (
        <span className="muted">No tasks yet</span>
      ) : (
        <>
          <span
            className="status-mix"
            aria-hidden="true"
            title={`${done} done, ${inProgress} in progress, ${total} in all`}
          >
            <span className="mix-done" style={{ width: `${share(done)}%` }} />
            <span className="mix-doing" style={{ width: `${share(inProgress)}%` }} />
          </span>
          <span className="progress-count">
            {done} of {total} done
          </span>
          {inProgress > 0 && <span className="progress-count">{inProgress} in progress</span>}
          {/* Said out loud, because it is the answer to "why does this still read planning when I
              finished three of them". Parked work is open work that is out of every list, so
              without this the counts beside the badge do not add up to the badge. */}
          {parked > 0 && <span className="progress-count">{parked} parked</span>}
        </>
      )}
      {progress.members > 1 && <span className="progress-count">{progress.members} people</span>}
      <span className="progress-count" title={readableInstant(progress.lastActivityAt, timezone)}>
        last change {relativeTime(progress.lastActivityAt)}
      </span>
    </p>
  );
}
